/**
 * Deep-link state for a detail modal: `?<param>=<id>`.
 *
 * Opening a modal pushes a history entry carrying the id, so the address bar
 * is shareable and Back closes the modal. Loading a URL that carries the
 * parameter opens that item on mount. Every other query parameter and the
 * hash are preserved - the host page (Webflow) owns those.
 *
 * One instance per parameter: `incidentUrl` (`?incident=`) and `documentUrl`
 * (`?document=`).
 */

export interface ModalUrl {
  param: string;
  /** Marker on history entries this instance pushed, so close can pop them. */
  marker: string;
  /** The id in `search`, or null when absent or not a plausible id. */
  read(search: string): string | null;
  /** `href` with the param set to `id`, or removed when `id` is null. */
  withParam(href: string, id: string | null): string;
  /** Record an opened modal: a new history entry, unless the URL already names it. */
  push(id: string): void;
  /**
   * Record a closed modal. If the current entry is one we pushed, step back to
   * the entry before it (so Back does not reopen the modal); otherwise - the
   * visitor arrived on a deep link - strip the param in place.
   */
  clear(): void;
}

const ID_RE = /^\d{1,7}$/;

export function modalUrl(param: string, marker: string): ModalUrl {
  const read = (search: string): string | null => {
    const raw = new URLSearchParams(search).get(param);
    const id = raw?.trim() ?? '';
    return ID_RE.test(id) ? id : null;
  };

  const withParam = (href: string, id: string | null): string => {
    const url = new URL(href);
    if (id === null) url.searchParams.delete(param);
    else url.searchParams.set(param, id);
    return url.toString();
  };

  const push = (id: string): void => {
    if (read(window.location.search) === id) return;
    window.history.pushState(
      { ...(window.history.state ?? {}), [marker]: id },
      '',
      withParam(window.location.href, id),
    );
  };

  const clear = (): void => {
    if (read(window.location.search) === null) return;
    if (window.history.state?.[marker]) {
      window.history.back();
      return;
    }
    const state = { ...(window.history.state ?? {}) };
    delete state[marker];
    window.history.replaceState(state, '', withParam(window.location.href, null));
  };

  return { param, marker, read, withParam, push, clear };
}
